import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import {
  AD_ACCOUNTS,
  BUTTON_CONNECTED,
  BUTTON_CONNECT_LINKEDIN,
  READY,
} from "../../i18n/keysTranslations";
import {
  ALERT_ICON_TYPE_SUCCESS,
  BORDER_RADIUS,
  FONT_SIZE_BUTTON,
  LINKEDIN,
} from "../../utils/constants";
import { logoPlatform } from "../../utils/string";
import { startSetLastSelectedIntegrationDataByPlatform } from "../../actions/user";
import { SimpleAlert } from "../Alerts/Alerts";
import ButtonLinkedinAction from "../Buttons/ButtonLinkedinAction";
import _ from "lodash";

const LinkedinContainerIntegration = ({
  textConnected = BUTTON_CONNECTED,
  id,
  sx = {},
  onClick = () => {},
  modeConnected = "add",
  modeDisconnected = "verify",
  onChangeIntegration = () => {},
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const onOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const onCloseMenu = () => setAnchorEl(null);

  const dispatch = useDispatchApp();
  const {
    isLoading,
    integrations,
    lastSelectedIntegrationData: {
      [LINKEDIN]: {
        adAccountID: adAccountIDSelected,
        integrationID: integrationIDSelected,
      } = {
        adAccountID: null,
        integrationID: null,
      },
    } = {},
  } = useSelectorApp((state) => state.user);
  const linkedInIntegrations = integrations?.[LINKEDIN] || {};
  const isLinkedInConnected = Object.values(linkedInIntegrations).some(
    (integration) => integration.isActive
  );

  const { t } = useTranslationApp();

  useEffect(() => {
    if (!integrationIDSelected && !adAccountIDSelected) {
      onPreSelectData();
    }

    onChangeIntegration({
      integrationID: integrationIDSelected,
      adAccountID: adAccountIDSelected,
    });
    // eslint-disable-next-line
  }, [integrationIDSelected, adAccountIDSelected]);

  const onChangeSelectedAdAccount = ({ integrationID, adAccountID }) =>
    dispatch(
      startSetLastSelectedIntegrationDataByPlatform({
        platform: LINKEDIN,
        adAccountID,
        integrationID,
      })
    );

  const onPreSelectData = () => {
    if (_.isEmpty(linkedInIntegrations)) return;

    const integrationID = Object.keys(linkedInIntegrations || {})[0];
    const adAccountID = Object.keys(
      linkedInIntegrations?.[integrationID]?.advertisers || {}
    )[0];

    onChangeSelectedAdAccount({
      integrationID,
      adAccountID,
    });
  };

  if (isLoading) return null;
  return (
    <>
      {isLinkedInConnected ? (
        <Box
          onClick={(e) => {
            onOpenMenu(e);
            onClick(e);
          }}
          sx={{
            backgroundColor: "#fff",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            display: "flex",
            alignItems: "center",
            px: 2,
            borderRadius: BORDER_RADIUS,
            gap: 1,
            width: "fit-content",
            cursor: "pointer",
            minHeight: 40,
            ...sx,
          }}
        >
          <AvatarMini src={logoPlatform(LINKEDIN)} />
          <Typography
            variant="body1"
            sx={{
              minWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "#000 !important",
              fontSize: FONT_SIZE_BUTTON,
            }}
          >
            {t(textConnected)}
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: "#000 !important" }} />
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Divider
              orientation="vertical"
              flexItem
              sx={{ backgroundColor: INPUT_COLOR_BORDER }}
            />
            <ButtonLinkedinAction
              id={id}
              type="icon"
              mode={modeConnected}
              icon={<AddIcon sx={{ color: "#000 !important" }} />}
              disableRipple={true}
              sx={{ mr: -2 }}
              onClick={() => {
                SimpleAlert({
                  title: t(READY),
                  icon: ALERT_ICON_TYPE_SUCCESS,
                });
              }}
            />
          </Box>
        </Box>
      ) : (
        <ButtonLinkedinAction
          id={id}
          mode={modeDisconnected}
          type="button"
          onClick={onClick}
          logo={LINKEDIN}
          text={BUTTON_CONNECT_LINKEDIN}
          sx={{
            ...sx,
          }}
        />
      )}
      {isLinkedInConnected && (
        <Menu anchorEl={anchorEl} open={open} onClose={onCloseMenu}>
          {Object.values(linkedInIntegrations).map((integration) => (
            <AccountIntegrationMenu
              key={integration.id}
              integration={integration}
            />
          ))}
        </Menu>
      )}
    </>
  );
};

const AccountIntegrationMenu = ({ integration = {} }) => {
  const { id, advertisers = {}, name } = integration;
  const advertisersKeys = Object.keys(advertisers);
  const { t } = useTranslationApp();

  const commonSxContainerType = {
    width: "100%",
    mt: 1,
    display: "flex",
    flexDirection: "column",
  };

  return (
    <MenuItem
      disableTouchRipple={true}
      disableRipple={true}
      sx={{
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          backgroundColor: "inherit",
          cursor: "default",
        },
        mb: 0.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          gap: 1,
        }}
      >
        <Typography
          variant="body1"
          textAlign={"left"}
          sx={{
            width: "fit-content",
          }}
        >
          {name || id}
        </Typography>
      </Box>
      {advertisersKeys.length > 0 && (
        <Box sx={commonSxContainerType}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
            }}
          >
            {t(AD_ACCOUNTS)}
          </Typography>
          {advertisersKeys.map((key) => (
            <Box
              key={advertisers[key].id}
              sx={{
                display: "flex",
                flexDirection: "column",
                mt: 1,
                pl: 3,
              }}
            >
              <Typography variant="body1">{advertisers[key].name}</Typography>
              <Typography
                variant="caption"
                sx={{
                  mt: -0.5,
                }}
              >
                {advertisers[key].id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </MenuItem>
  );
};

const AvatarMini = ({ src, sx }) => (
  <Avatar
    alt="Logo de LinkedIn"
    src={src}
    variant="square"
    sx={{
      height: 20,
      width: 20,
      "& .MuiAvatar-img": {
        objectFit: "contain",
      },
      ...sx,
    }}
  />
);

export default LinkedinContainerIntegration;
